import moment from "moment"
import { joinNames, plural, quantityLabel } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { datesLabel } from "../Sheets/sheetLabels"
import { balanceNumber } from "../Stock/stockForm"

const monthOf = month => moment(month, "YYYY-MM", true)
// "December 2026", "Dec 2026": a month of the forecast.
export const monthName = month => monthOf(month).format("MMMM YYYY")
export const monthShort = month => monthOf(month).format("MMM YYYY")
// A month's column: "Dec*" for the current one, counted from today, then "Jan", "Feb"…
export const columnLabel = (month, index) => `${monthOf(month).format("MMM")}${index ? "" : "*"}`
// "Dec* = 10–31 Dec 2026.": the days the current month's column counts.
export const partialNote = ({ month, from, to }) => `${columnLabel(month, 0)} = ${datesLabel(from, to)}.`
// "last year 14 · 96 cans": the events of the same month last year and the balls their calculation consumed (ticket 29 Q6); "—" before any
// calculation.
export const lastYearLabel = (lastYear, cans) => `last year ${lastYear.events ?? "—"}${lastYear.events != null && cans != null
  ? ` · ${quantityLabel(cans, "box")}` : ""}`
// "11 + 6 est.": a month's certain and estimated parts, when some is estimated.
export const splitLabel = ({ certain, estimated }) => (estimated ? `${certain} + ${estimated} est.` : null)
// "→ −5": the balance left at a month's end, red below 0 and amber below an active product's minimum (ticket 29 Q5).
export const endLabel = balance => `→ ${balanceNumber(balance)}`
export const endColor = (product, balance) => (balance < 0 ? "text-danger"
  : product.active && product.minimum != null && balance < product.minimum ? "text-warning" : "text-muted")

// How long a place's balance lasts (ticket 29 Q5): until the month before the first one it ends short, or beyond the forecast, then
// perhaps below its minimum from a month; nothing before the initial inventory.
export const coverage = ({ balance, short, belowMinimum }, months) => {
  if (balance === null) return null
  const beyond = `Beyond ${monthShort(months[months.length - 1].month)}`
  const index = months.findIndex(({ month }) => month === short)
  if (index === 0) return { label: "Insufficient now", color: "soft-danger", detail: `short in ${monthName(short)}` }
  if (index > 0) return { label: `Until ${monthShort(months[index - 1].month)}`, color: "soft-danger", detail: `short in ${monthName(short)}` }
  if (belowMinimum) return { label: beyond, color: "soft-warning", detail: `below minimum from ${monthName(belowMinimum)}` }
  return { label: beyond, color: "soft-success", detail: null }
}

// "1 disabled event and 1 disabled competition are not included.": what the forecast leaves out (US 127).
export const excludedNote = ({ events, competitions }) => {
  const parts = [events && plural(events, "disabled event"), competitions && plural(competitions, "disabled competition")].filter(Boolean)
  return parts.length ? `${joinNames(parts)} ${events + competitions === 1 ? "is" : "are"} not included.` : null
}

const SOURCES = { admin: { label: "Admin set", color: "soft-primary" }, calculated: { label: "Calculated", color: "soft-success" },
  estimated: { label: "Estimated", color: "soft-warning" } }
// Where an event's quantity comes from (ticket 29 Q7): an estimate without similar competitions uses the full capacity, to check.
export const sourceBadge = (item, occupancy) => {
  if (item.source === "supplementary") return { label: `Supplementary #${item.shipment}`, color: "soft-info" }
  if (item.source === "estimated" && occupancy?.level === "fixed") return { label: "Estimated · too little history", color: "soft-danger" }
  return SOURCES[item.source] || { label: item.source, color: "soft-secondary" }
}
const LEVELS = { organizer: "organizer", global: "global", relaxed: "global, relaxed", fixed: "full capacity, to check" }
// "occupancy · organizer · 12 of 16 expected, 4 registered": what an estimate stands on.
export const occupancyLabel = ({ level, participants, expected, capacity }) =>
  `occupancy · ${LEVELS[level] || level} · ${expected} of ${capacity} expected, ${participants} registered`
// "2 · 8": the quantity on the current registrations and at full capacity; nothing for a supplementary shipment.
export const nowAndMaxLabel = ({ calculated, benchmark }) => (calculated == null ? "—" : `${calculated} · ${benchmark}`)
